import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import assistantRiaImg from '../assets/assistant_ria.jpeg';
import ReactMarkdown from 'react-markdown';
// @ts-ignore
import remarkGfm from 'remark-gfm';
// @ts-ignore
import remarkBreaks from 'remark-breaks';
import { supabasePublic } from '../lib/supabasePublic';
import { useAuth } from '../contexts/AuthContext';
import {
  ASSISTANT_RIA_SUSPENDED,
  AssistantRIASuspensionNotice,
} from '../components/AssistantRIASuspensionNotice';

const MAX_HISTORY = 5;

const loadingStagesConfig = [
  { stage: 'thinking', delay: 0 },
  { stage: 'synthesizing', delay: 3500 },
  { stage: 'finishing', delay: 10000 },
  { stage: 'doubleChecking', delay: 20000 },
  { stage: 'coffeeBreak', delay: 30000 },
  { stage: 'stillWorking', delay: 45000 }
] as const;

type LoadingStage = typeof loadingStagesConfig[number]['stage'] | 'idle';

const loadingMessages: Record<Exclude<LoadingStage, 'idle'>, string> = {
  thinking: "Je rassemble les informations nécessaires...",
  synthesizing: "Je synthétise votre réponse, cela arrive dans un instant.",
  finishing: "Encore quelques secondes, je finalise votre réponse.",
  doubleChecking: "Je relis pour éviter les hallucinations... la vigilance humaine, toujours !",
  coffeeBreak: "Merci pour ta patience, je bois un rapide café de données ☕.",
  stillWorking: "Toujours en train de réfléchir... tu peux me laisser finir ou relancer une nouvelle question."
};

/**
 * Nettoie le texte pour supprimer les caractères indésirables et corriger les problèmes d'encodage
 */
function cleanAnswerText(text: string): string {
  return text
    // Supprimer les caractères de contrôle non-standard
    .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')
    // Corriger les problèmes d'encodage courants
    .replace(/â€™/g, "'")
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/â€"/g, '—')
    .replace(/â€"/g, '–')
    .replace(/â€¦/g, '…')
    // Normaliser les espaces multiples
    .replace(/[ \t]+/g, ' ')
    // Normaliser les sauts de ligne multiples (garder max 2)
    .replace(/\n{3,}/g, '\n\n')
    // Supprimer les espaces en début/fin de ligne
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    .trim();
}

const AssistantRIAConversationPage = () => {
  const { isAdherent } = useAuth();
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<{question: string, answer: string}[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('idle');
  const [limitError, setLimitError] = useState<string | null>(null);

  // Fonction pour gérer le compteur de questions par jour pour les non-adhérents
  const getQuestionCountToday = (): number => {
    const today = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD
    const key = `assistant-ria-questions-${today}`;
    const stored = localStorage.getItem(key);
    return stored ? parseInt(stored, 10) : 0;
  };

  const incrementQuestionCount = (): void => {
    const today = new Date().toISOString().split('T')[0];
    const key = `assistant-ria-questions-${today}`;
    const currentCount = getQuestionCountToday();
    localStorage.setItem(key, (currentCount + 1).toString());
  };

  const canAskQuestion = (): boolean => {
    // Les adhérents n'ont pas de limite
    if (isAdherent()) {
      return true;
    }
    // Les non-adhérents sont limités à 2 questions par jour
    return getQuestionCountToday() < 2;
  };

  const getRemainingQuestions = (): number => {
    if (isAdherent()) {
      return Infinity; // Pas de limite pour les adhérents
    }
    return Math.max(0, 2 - getQuestionCountToday());
  };

  // Sauvegarder la question dans Supabase (optionnel, pour analytics)
  const saveQuestionToSupabase = async (question: string) => {
    try {
      console.log('💾 Sauvegarde de la question dans Supabase');
      const { error } = await supabasePublic.from('assistant_ria').insert([{ question }]);
      if (error) {
        console.error('❌ Erreur sauvegarde question:', error);
      } else {
        console.log('✅ Question sauvegardée avec succès');
      }
    } catch (error) {
      // On continue même si la sauvegarde échoue
      console.error('❌ Erreur sauvegarde question:', error);
    }
  };

  // Appel à l'API Gemini via la route API Vercel (qui appelle Supabase)
  const callGeminiAPI = async (question: string, history: {question: string, answer: string}[]) => {
    const recentHistory = history.slice(-MAX_HISTORY);
    
    console.log('📤 Appel de la route API assistant-ria:', {
      question: question.substring(0, 50),
      historyLength: recentHistory.length
    });

    try {
      // Appeler notre route API Vercel au lieu de Supabase directement
      const response = await fetch('/api/assistant-ria', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ question, history: recentHistory }),
    });
      
      console.log('📥 Réponse reçue:', {
        status: response.status,
        ok: response.ok
      });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erreur serveur' }));
        console.error('❌ Erreur HTTP:', errorData);
        throw new Error(errorData.error || `Erreur ${response.status}`);
    }
    
    const data = await response.json();
      console.log('✅ Données reçues:', { hasAnswer: !!data.answer });
      
      if (!data || !data.answer) {
        throw new Error('Réponse invalide de la fonction');
    }
      
    return data.answer;
    } catch (error) {
      console.error('❌ Erreur lors de l\'appel API:', error);
      throw error;
    }
  };

  const handleAsk = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const userQuestion = question;
    if (!userQuestion.trim()) return;

    // Vérifier la limite pour les non-adhérents
    if (!canAskQuestion()) {
      setLimitError('Vous avez atteint la limite de 2 questions par jour.');
      return;
    }

    setLimitError(null);
    setIsLoading(true);
    console.log('🚀 Début de handleAsk:', { question: userQuestion });
    try {
      // Incrémenter le compteur avant d'envoyer (même si ça échoue, on compte la tentative)
      incrementQuestionCount();
      await saveQuestionToSupabase(userQuestion);
      const answer = await callGeminiAPI(userQuestion, history);
      console.log('✅ Réponse obtenue avec succès');
      setHistory(prev => [...prev, { question: userQuestion, answer }]);
    } catch (e) {
      console.error('❌ Erreur dans handleAsk:', e);
      const errorMessage = e instanceof Error ? e.message : 'Erreur inconnue';
      setHistory(prev => [...prev, { 
        question: userQuestion, 
        answer: `Erreur: ${errorMessage}. Le service est momentanément saturé en raison d'un grand nombre de demandes. Merci de réessayer dans quelques instants.` 
      }]);
    }
    setQuestion('');
    setIsLoading(false);
  };

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 1200);
  };

  // Auto-expand textarea
  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value);
    // Effacer le message d'erreur quand l'utilisateur tape
    if (limitError) {
      setLimitError(null);
    }
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  // Envoi avec Ctrl+Entrée ou Cmd+Entrée
  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleAsk();
    }
  };

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    if (isLoading) {
      loadingStagesConfig.forEach(({ stage, delay }) => {
        const timer = setTimeout(() => setLoadingStage(stage), delay);
        timers.push(timer);
      });
    } else {
      setLoadingStage('idle');
    }

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [isLoading]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-start bg-white py-12 rounded-3xl">
      {ASSISTANT_RIA_SUSPENDED && (
        <div className="w-full max-w-xs sm:max-w-2xl lg:max-w-4xl px-2 sm:px-0 mb-6">
          <AssistantRIASuspensionNotice showContactLink={false} />
          <p className="mt-3 text-sm text-center text-gray-600">
            <Link to="/assistant-ria" className="text-[#774792] underline hover:no-underline">
              Retour à la page de l&apos;assistant RIA
            </Link>
          </p>
        </div>
      )}
      {/* Image uniquement si aucune question posée */}
      {history.length === 0 && !ASSISTANT_RIA_SUSPENDED && (
        <img
          src={assistantRiaImg}
          alt="Assistant RIA"
          className="w-24 h-24 mb-8 rounded-full shadow-md object-cover"
        />
      )}
      {/* Historique des échanges */}
      <div className="w-full max-w-xs sm:max-w-2xl lg:max-w-4xl mt-4 flex flex-col items-center px-2 sm:px-0">
        {history.map((item, idx) => (
          <div key={idx} className="mb-6 w-full">
            <div className="font-semibold text-[#774792] flex items-center gap-2 mb-1">Vous :
              <button
                onClick={() => handleCopy(item.question, idx)}
                className="ml-2 px-2 py-0.5 rounded-full bg-[#f6f0fa] hover:bg-[#ede6fa] transition text-[#a58fd6] text-[11px] font-normal flex items-center gap-1 border border-[#e5d8fa] shadow-none hover:shadow focus:outline-none"
                title="Copier la question"
              >
                <span className="text-xs">📋</span>
                {copiedIndex === idx ? (
                  <span>Copié !</span>
                ) : (
                  <span>Copier la question</span>
                )}
              </button>
            </div>
            <div className="bg-white rounded-xl border border-[#d1b3f7] px-4 py-2 mb-2 text-gray-800">{item.question}</div>
            <div className="font-semibold text-blue-800 flex items-center gap-2 mb-1">Assistant RIA :
              <button
                onClick={() => handleCopy(item.answer, idx)}
                className="ml-2 px-2 py-0.5 rounded-full bg-[#f6f0fa] hover:bg-[#ede6fa] transition text-[#a58fd6] text-[11px] font-normal flex items-center gap-1 border border-[#e5d8fa] shadow-none hover:shadow focus:outline-none"
                title="Copier la réponse"
              >
                <span className="text-xs">📋</span>
                {copiedIndex === idx ? (
                  <span>Copié !</span>
                ) : (
                  <span>Copier la réponse</span>
                )}
              </button>
            </div>
            <div className="bg-white rounded-xl border border-[#d1b3f7] px-4 py-2 text-gray-700 prose-ria max-w-none
              [&>p]:mb-6 [&>p]:leading-relaxed
              [&>ul]:mb-6 [&>ul]:ml-6 [&>ul]:list-disc [&>ul]:space-y-2
              [&>ol]:mb-6 [&>ol]:ml-6 [&>ol]:list-decimal [&>ol]:space-y-2
              [&>ul>li]:ml-2 [&>ol>li]:ml-2
              [&>ul>li>ul]:mt-2 [&>ul>li>ul]:ml-6 [&>ul>li>ul]:list-disc
              [&>ol>li>ol]:mt-2 [&>ol>li>ol]:ml-6 [&>ol>li>ol]:list-decimal
              [&>strong]:font-semibold [&>strong]:text-gray-900
              [&>em]:italic [&>em]:text-gray-700">
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={{
                  a: (props) => (
                    <a
                      {...props}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#774792] underline hover:text-violet-900 transition-colors"
                    >
                      {props.children}
                    </a>
                  ),
                  p: ({ children }) => <p className="mb-6 leading-relaxed">{children}</p>,
                  ul: ({ children, depth }) => (
                    <ul className={`mb-6 ${depth > 0 ? 'ml-6 mt-2' : 'ml-6'} list-disc space-y-2`}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children, depth }) => (
                    <ol className={`mb-6 ${depth > 0 ? 'ml-6 mt-2' : 'ml-6'} list-decimal space-y-2`}>
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="ml-2 leading-relaxed">{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-gray-900">{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic text-gray-700">{children}</em>
                  ),
                }}
              >
                {cleanAnswerText(item.answer).replace(/\n/g, '\n\n')}
              </ReactMarkdown>
            </div>
          </div>
        ))}
      </div>
      {/* Message d'erreur de limite */}
      {limitError && (
        <div className="w-full max-w-xs sm:max-w-2xl lg:max-w-4xl mt-4 mb-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {limitError}{' '}
          <Link to="/inscription" className="text-red-800 font-semibold hover:underline">
            Devenez adhérent
          </Link>
          {' '}pour poser des questions illimitées !
        </div>
      )}

      {/* Indicateur de questions restantes pour les non-adhérents */}
      {!ASSISTANT_RIA_SUSPENDED && !isAdherent() && (
        <div className="w-full max-w-xs sm:max-w-2xl lg:max-w-4xl mt-2 mb-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-sm text-center">
          {getRemainingQuestions() > 0 ? (
            <>Questions restantes aujourd'hui : <strong>{getRemainingQuestions()}</strong> / 2</>
          ) : (
            <>
              Vous avez atteint votre limite quotidienne.{' '}
              <Link to="/inscription" className="text-blue-800 font-semibold hover:underline">
                Devenez adhérent
              </Link>
              {' '}pour des questions illimitées !
            </>
          )}
        </div>
      )}

      {/* Zone de saisie toujours en bas */}
      {!ASSISTANT_RIA_SUSPENDED && (
      <form
        onSubmit={handleAsk}
        className="w-full max-w-xs sm:max-w-2xl lg:max-w-4xl mt-6 bg-[#f6f0fa] rounded-xl border-2 border-[#774792] shadow-sm px-2 sm:px-4 py-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
        style={{ position: 'sticky', bottom: 0, zIndex: 10 }}
      >
        <textarea
          ref={textareaRef}
          className={`flex-1 px-4 py-2 rounded-xl border-0 focus:outline-none resize-none ${history.length > 0 ? 'min-h-[36px]' : 'min-h-[80px]'} sm:min-h-[44px] text-lg bg-transparent leading-[1.7] flex items-center`}
          placeholder={!canAskQuestion() ? "Limite quotidienne atteinte. Devenez adhérent pour continuer." : "Posez votre question"}
          value={question}
          onChange={handleTextareaInput}
          onKeyDown={handleTextareaKeyDown}
          disabled={isLoading || !canAskQuestion()}
          rows={1}
          required
          style={{resize: 'none', display: 'flex', alignItems: 'center'}}
        />
        <button
          type="submit"
          className="sm:ml-2 p-2 rounded-full bg-gradient-to-r from-indigo-500 to-[#774792] text-white shadow hover:shadow-md transition-all duration-300 disabled:opacity-60 flex items-center justify-center self-end sm:self-auto"
          disabled={isLoading || !question.trim() || !canAskQuestion()}
          aria-label="Envoyer (Ctrl+Entrée ou Cmd+Entrée)"
          title="Envoyer (Ctrl+Entrée ou Cmd+Entrée)"
        >
          {isLoading ? (
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" /></svg>
          )}
        </button>
      </form>
      )}
      {/* Message et bouton pour nouvelle discussion si l'historique dépasse MAX_HISTORY */}
      {history.length > MAX_HISTORY && (
        <div className="flex flex-col items-center mt-2">
          <div className="text-sm text-gray-600 mb-2 text-center">
            Seuls les 5 derniers échanges sont pris en compte pour chaque nouvelle question.<br />
            Pour repartir sur une nouvelle base, commencez une nouvelle discussion.
          </div>
          <button
            className="px-4 py-2 rounded bg-gradient-to-r from-indigo-500 to-[#774792] text-white shadow hover:shadow-md transition-all duration-300 text-sm"
            onClick={() => setHistory([])}
          >
            Commencer une nouvelle discussion
          </button>
        </div>
      )}
      {isLoading && loadingStage !== 'idle' && (
        <div className="text-center text-sm text-gray-500 mt-2 max-w-xs mx-auto">
          {loadingMessages[loadingStage] || "Merci de patienter pendant que je prépare votre réponse."}
        </div>
      )}
    </div>
  );
};

export default AssistantRIAConversationPage; 